import React, { useEffect, useState } from 'react';
import { ShieldCheck, X } from 'lucide-react';

interface VerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVerify: (details: { businessName: string; taxId: string }) => void;
}

const VerificationModal: React.FC<VerificationModalProps> = ({ isOpen, onClose, onVerify }) => {
  const [businessName, setBusinessName] = useState('');
  const [taxId, setTaxId] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (businessName && taxId) {
      onVerify({ businessName, taxId });
    }
  };

  const inputClass =
    'w-full p-3 bg-paper-bright border border-ink-200 text-sm text-ink-900 placeholder:text-ink-500 focus:border-ember focus:outline-none focus:ring-1 focus:ring-ember/20 transition-colors duration-150';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-forge-deep/80 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative bg-paper-bright shadow-float w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-300 ease-editorial">

        {/* Header */}
        <div className="px-6 py-5 border-b border-ink-200 flex items-center justify-between">
          <div className="inline-flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5 text-ember" strokeWidth={1.5} />
            <span className="font-mono text-[10px] tracking-micro uppercase text-ember">
              Wholesale access
            </span>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-ink-500 hover:text-ink-950 transition-colors duration-150"
            aria-label="Close"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 lg:p-8">
          <h2 className="font-editorial italic font-light text-3xl text-ink-950 leading-tight tracking-tight mb-3">
            Business verification.
          </h2>
          <p className="text-sm text-ink-700 leading-relaxed mb-8 max-w-prose">
            To view wholesale pricing and submit orders, please verify your business credentials.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">
                Business name
              </label>
              <input
                type="text"
                required
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                className={inputClass}
                placeholder="Island Hardware Supply"
              />
            </div>

            <div>
              <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">
                Tax ID / business number
              </label>
              <input
                type="text"
                required
                value={taxId}
                onChange={(e) => setTaxId(e.target.value)}
                className={inputClass}
                placeholder="123456789 RT0001"
              />
            </div>

            <button
              type="submit"
              className="w-full py-3.5 bg-ember hover:bg-ember-deep text-paper-warm text-sm font-medium transition-colors duration-150 ease-editorial mt-2"
            >
              Verify & continue
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VerificationModal;
